<script>
import { FormButton, ToggleButton } from "@/components/form";
import LoadingView from "@/components/LoadingView.vue";
import { AttachmentsPane, InfoPane } from "@/components/agents";
import { LogPane } from "@/components/shared";
import TabPanes from "@/components/TabPanes.vue";
import Agent from "@/models/agent";
import { GlobalPolicy } from "@/policies/global";
import { useAuthStore } from "@/stores/auth";
import axios from "axios";
import { mapState } from "pinia";
import { agentStatuses } from "@/helpers/globals";
import ModalDialog from "@/components/ModalDialog.vue";
import InfoText from "@/components/media/InfoText.vue";

let tabs = [
    {
        name: 'info',
        icon: 'fa-alert',
        title: 'General Information',
        acl: 0
    },
    {
        name: 'attachments',
        icon: 'fa-link',
        title: 'Attached Locations',
        acl: 0
    },
    {
        name: 'logs',
        icon: 'fa-history',
        title: 'History',
        acl: 1
    }
];
export default {
    head() {
        return {
            title: () => `${this.$route.meta.title} %separator ${this.agent?.alt ?? 'Untitled'}`
        }
    },
    setup() {
        return {
            GlobalPolicy,
            statuses: agentStatuses     
        }
    },
    data() {
        return {
            id: this.$route.params.id,
            agent: new Agent({id: this.id}),
            isLoading: true,
            tabs: [],
            deleting: false,
            prev: false
        }
    },
    methods: {
        show() {
            axios.get(`agents/${this.id}`)
                .then(res => {
                    this.agent = new Agent(res.data);
                    this.isLoading = false;
                })
                .catch(err => {
                    if(err.response.status === 404) 
                        handle404(this, err.response.data.message);
                });
        },
        save() {
            this.isLoading = true;
            axios.postForm(`agents/${this.agent.id}?_method=PATCH`, this.agent)
                .then(res => {
                    this.agent = new Agent(res.data);
                    this.isLoading = false;
                })
                .catch(e => {
                    throw e;
                });
        },
        prepare(value) {
            this.agent = new Agent(value);
            this.save();
        },
        openDelete() {
            this.deleting = true;
        },
        cancel() {
            this.deleting = false;
        },
        purge() {
            axios.delete(`agents/${this.agent.id}/purge`)
                .then(() => {
                    this.$router.go(-1);
                })
                .catch(err => {
                    console.error(err);
                })
        },
        status() {
            if(this.agent.status == 'active') {
                axios.delete(`agents/${this.agent.id}`)
                    .then(() => {
                        this.agent.status = 'inactive';
                    })
                    .catch(err => {
                        console.error(err);
                    })
            } else {
                axios.patch(`agents/${this.agent.id}/restore`)
                    .then(() => {
                        this.agent.status = 'active';
                    })
                    .catch(err => {
                        console.error(err);
                    })
            }
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user'])
    },
    created() {
        this.show();
        this.tabs = tabs.filter(tab => {
            return this.user.level >= tab.acl
        });
    },
    beforeRouteEnter(to,from,next) {
        next(vm => {
            if(!vm.prev) vm.prev = (from.name && from) ?? false;
        })
    },
    components: {
        LoadingView,
        FormButton,
        TabPanes,
        AttachmentsPane,
        InfoPane,
        LogPane,
        ToggleButton,
        ModalDialog,
        InfoText
    }
}
</script>

<template>
    <LoadingView v-if="isLoading" />
    <div v-else class="flex flex-col gap-8">
        <nav class="flex flex-none gap-4 justify-between items-center rounded-t-lg">
            <div class="flex-none">
                <FormButton
                    color="secondary"
                    icon="fa-chevron-left"
                    @click="prev ? $router.push(prev) : $router.go(-1)">
                    Back
                </FormButton>
            </div>
            <div class="py-4 sm:rounded-tr-lg flex justify-end items-center h-16 gap-2">
                <ToggleButton v-if="agent.id && GlobalPolicy.edit()"
                    :id="`item-${agent.id}-status`"
                    on-color="green"
                    @changed="status()"
                    :checked="agent.status == 'active'"
                    label="Set Status" />
                <FormButton v-if="agent.id && GlobalPolicy.edit()" prefix-class="fak" icon="fa-trash" @click="openDelete()" color="red"/>
            </div>
        </nav>
        <header class="flex items-start gap-8">
            <div class="flex flex-col lg:flex-row gap-5 sm:gap-6 flex-1">
                <div class="flex flex-col gap-5 sm:gap-6 flex-1 justify-between">
                    <div class="flex flex-col lg:flex-row gap-5 justify-between flex-none">

                        <div class="flex-1 flex flex-col gap-2 lg:gap-0 order-2 lg:order-1">
                            <h1 class="text-3xl leading-9 font-medium font-headline antialiased bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text inline-block">
                                {{ agent.name }}
                            </h1>
                        </div>
                    </div>
                    <section class="flex flex-row w-full gap-2">
                        <h2 class="sr-only col-span-full">Basic Information</h2>
                        <InfoText v-if="agent?.email" class="w-full" label="Email" icon="fak fa-email" :value="agent.email" bg-color="gray" :link="`mailto:${agent.email}`" />
                        <InfoText v-if="agent?.meta?.phone" class="w-full" label="Phone" icon="fak fa-call" :value="agent?.meta?.phone" bg-color="gray" :link="`tel:${agent?.meta?.phone}`" />
                    </section>

                </div>
                <div class="w-full lg:w-1/5 flex">
                    <img v-if="agent.photo_url" class="object-cover rounded-lg"
                        :src="agent.photo_url" :alt="agent.name"
                        :title="agent.name">
                    <div v-else class="text-5xl bg-bhav-blue-500 rounded-lg text-white p-8 flex items-center justify-center w-full">
                        <span class="far fa-user"></span>
                    </div>
                </div>

            </div>
        </header>
        <TabPanes route="agent-tab" :tabs="tabs" v-slot="{tab}" via="via-white" to="to-white" class="gap-4 py-6">
            <InfoPane :item="agent" @update="prepare" v-if="tab?.name === 'info'" />
            <AttachmentsPane :id="agent.id" v-if="tab?.name === 'attachments'" />
            <LogPane context="agent" v-if="tab?.name === 'logs'" />
        </TabPanes>
    </div>

    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-600 font-semibold">Confirm Sales Team Member Deletion</h2>
        </template>
        <template #body>
            <p class="text-bhav-blue-500"><strong>Attention!</strong> You are about to delete <em>{{ agent?.first_name }} {{ agent?.last_name }}</em>.</p>
            <p class="mt-2"><strong>This will detach this person from all collections and <em>permanently</em> delete their record.</strong></p>
            <p class="mt-2">Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="purge" prefix-class="fak" icon="fa-trash" color="red"/>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
</template>